import '@progress/kendo-theme-default/dist/all.css';
import styles from "./GridComponent.module.css";
import React, { useEffect, useState, useRef, useCallback } from 'react';

import { getter } from "@progress/kendo-react-common";
import { Grid, GridColumn as Column, GridToolbar, GridNoRecords, getSelectedState/*, GridColumnMenuFilter*/, GridColumnMenuWrapper, isColumnMenuFilterActive} from "@progress/kendo-react-grid";
import { process as dataProcess} from '@progress/kendo-data-query';
//import { Tooltip } from '@progress/kendo-react-tooltip';

//show/hide columns
import { CustomColumnMenu } from "./CustomColumnMenu";

import { EDIT_FIELD, SELECTED_FIELD, DATA_ITEM_KEY, FILTER_OPERATORS/*, FILTERTYPE_CHECKBOX*/ } from "./GridConstants";

import BulkEditForm from './bulkEdit/BulkEditForm';
import { ColumnMenu/*, ColumnMenuCheckboxFilter*/ } from './columnFilter/columnMenu';

const GRID_PAGE_SIZE = +process.env.REACT_APP_GRID_PAGE_SIZE || 10;

let resizeTimeout = null;

const initialDataState = {
    sort: [{
        field: "name",
        dir: "asc",
    }],
    take: GRID_PAGE_SIZE, 
    skip: 0,
};

const GridComponent = props => {
    const idGetter = getter(DATA_ITEM_KEY);

//    let allDataForCheckboxFilter = props.data;

    const gridRef = useRef(null);
    const minGridWidth = useRef(0);

    const [useMinWidth, setUseMinWidth] = useState(false);
    //recommended workaround from Telerik to deal with resize bug -> setting a different key will force rerender the grid
    const [gridUpdateColumnWorkAroundKey, setGridColumnUpdateWorkAroundKey] = useState(false); 
    
    const {columnConfigs, dataReloaded} = props;
    const [columns, setColums] = useState([]);

    //For column show/hide
    const [stateColumns, setStateColumns] = useState(columnConfigs);

    const [currentSelectedState, setCurrentSelectedState] = React.useState({});
    const [dataState, setDataState] = useState(initialDataState);
    const [dataResult, setDataResult] = useState(dataProcess(props.data.map(item => ({
                                                            ...item,
                                                            [SELECTED_FIELD]: currentSelectedState[idGetter(item)]
                                                        }
                                                        )), dataState));


    const onDataStateChange = e => {
        if(e.targetEvent && e.targetEvent.value === "All"){
            e.dataState.take = props.data.length;
        }

        const newDataState = dataProcess(props.data.map(item => ({
            ...item,
            [SELECTED_FIELD]: currentSelectedState[idGetter(item)]
        }
        )), e.dataState);

        setDataState(e.dataState);
        setDataResult(newDataState);
    };

    /* without this edit does not seem to work - filter is being reset using dataReloaded prop */
    useEffect(() => {
        setDataResult(dataProcess(props.data, dataState));
    }, [props.data, dataState]);



    //For bulk edit dialog
    const [selectedData, setSelectedData] = useState([]);   //List of user to edit
    const [bulkEditVisible, setBulkEditVisible] = useState(false);
    const [bulkEditEnabled, setBulkEditEnabled] = useState(false);

    
    const toggleBulkEditDialog = () => {
        setBulkEditVisible(!bulkEditVisible);
    };


    const setSelectedValue = (dataResult) => {
        if(dataResult.data){
            dataResult.data = dataResult.data.map((item) => {
                return {
                    ...item,
                    [SELECTED_FIELD]: currentSelectedState[idGetter(item)],
                };
            });
        }
        return dataResult;
    };

    const onSelectionChange = useCallback((event) => {
          const newSelectedState = getSelectedState({
            event,
            selectedState: currentSelectedState,
            dataItemKey: DATA_ITEM_KEY,
          });
          setCurrentSelectedState(newSelectedState);
    }, [currentSelectedState]);

    const onHeaderSelectionChange = useCallback((event) => {
//        console.log("OnHeaderSelectionChange");
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;

        const newSelectedState = {...currentSelectedState};
        event.dataItems.forEach((item) => {
          newSelectedState[idGetter(item)] = checked;
        });
        setCurrentSelectedState(newSelectedState);
    }, [idGetter, currentSelectedState]);


    const onColumnsSubmit = (columnsState) => {
//        console.log("onColumnsSubmit");
        //Never hide locked columns (like: Username & Name)
        let fixedColumns = columnConfigs.filter((col) => col.locked)

        setStateColumns([...fixedColumns, ...columnsState]);
    };

    const onColumnsReset = () => {
        console.log("onColumnsReset");
        //Reset -> back to pre-selected columns
        setStateColumns(columnConfigs);
    };
    
  
    const handleResize = useCallback(() => {
        console.log("handleResize");
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            if (!gridRef.current) return;
            const isGridSmallerThanMinWidth = gridRef.current.offsetWidth < minGridWidth.current;
            setUseMinWidth(isGridSmallerThanMinWidth);
        },200);
    }, [gridRef, minGridWidth, setUseMinWidth]);


    //Find selected rows in data for use in bulk edit form
    useEffect(() => {
//        console.log("useEffect - [currentSelectedState, props.data]");
        setSelectedData(Object.keys(currentSelectedState)
                .filter(item => currentSelectedState[item] === true)
                .map((row) => { 
                    return props.data.find(item => item.id === row);
                })
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        );

        setBulkEditEnabled(hasSelectedRows(currentSelectedState));
    }, [currentSelectedState, props.data]);


    //Resetting things when grid data refreshed
    useEffect(() => {
//console.log("----GridComponent - UseEffect - ColumnConfigs+dataReloaded");

        //Reset columns
        setStateColumns(columnConfigs);

        //Reset selected
        setCurrentSelectedState({});

        ///Reset initial state
        setDataState(initialDataState);

    }, [columnConfigs, dataReloaded]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            window.removeEventListener("resize", handleResize);
        }
    }, [handleResize]);
   

    useEffect(() => {
//console.log("----GridComponent - stateConfigs - props.gridId - gridRef - setUseMinWidth");

        if (!props.gridId) return;
        gridRef.current = document.querySelector(`#${props.gridId}`);
        if (!gridRef.current) return;
        
        let totalMinColumnWidth = 0;
        stateColumns.forEach((config) => {
            if (!config) return;
            if (config.width) {
                totalMinColumnWidth += (+config.width);
            } else if (config.minWidth) {
                totalMinColumnWidth += (+config.minWidth);
            } else if (config.minResizableWidth) {
                totalMinColumnWidth += (+config.minResizableWidth);
            }
        });

        minGridWidth.current = totalMinColumnWidth;
        setUseMinWidth(gridRef.current.offsetWidth < totalMinColumnWidth);
    }, [stateColumns, props.gridId, gridRef, setUseMinWidth]);


    useEffect(() => {
//console.log("----GridComponent - columnConfigs - useMinWidth - setColums");
        setColums(generateColumns(columnConfigs, useMinWidth));
    }, [columnConfigs, useMinWidth, setColums]);

    useEffect(() => {
//console.log("----GridComponent - Columns - setGridColumnUpdateWorkAroundKey");
        setGridColumnUpdateWorkAroundKey(state => !state);
    }, [columns, setGridColumnUpdateWorkAroundKey]);


    useEffect(() => {
//console.log("----GridComponent - StateColumns - useMinwidth - setColums-----");
        setColums(generateColumns(stateColumns, useMinWidth));
    }, [stateColumns, useMinWidth, setColums]);

    useEffect(() => {
//console.log("-----GridComponent - columnConfigs----");
//        console.log(columnConfigs);
        //If there are items in columnConfigs that aren't in stateColumns -> move
        setStateColumns(columnConfigs);
    }, [columnConfigs]);

    
    const onItemChangedHandler = (event) => {
//console.log("GridComponenent - onItemChangedHandler", event);
//console.log(event);
        if (!event.field) return;
        if (!event.dataItem) return;
        const field = event.field;
        
        if (props.changeValidator && !props.changeValidator(event.dataItem, field, event.value)) return;
        event.dataItem[field] = event.value;

        const updatedData = props.data.map(item => {
            
            if (item.id === event.dataItem.id) {
                const newItem = JSON.parse(JSON.stringify(item));

                newItem[field] = event.value;
                if (!newItem.changedItems) {
                    newItem.changedItems = [];
                } else {
                    newItem.changedItems = newItem.changedItems.filter(id => {
                        return newItem[id] !== newItem.originalValues[id];
                    });
                }

                if (!newItem.originalValues || newItem.originalValues[field] !== newItem[field]) {
                    newItem.changedItems.push(field);
                }
            
                return newItem;
            }
    
            return item;
        });
        props.onChange(updatedData);
    };

    const handleCancelBulkEdit = (event) => {
        setBulkEditVisible(false);
        
    }

    const updateColumnVisibility = (event) => {
        let newStateColumns = stateColumns;
        if(event && event.fields){

            Object.keys(event.fields).forEach(fieldName => {
                newStateColumns = newStateColumns.map((column, idx) => {
                    return column.field === fieldName ? {
                        ...column,
                        show: true
                    } : column;
                    });
            });
        }

        if(event && event.removedFields){
            Object.keys(event.removedFields).forEach(fieldName => {
                newStateColumns = newStateColumns.map((column, idx) => {
                    return column.field === fieldName ? {
                        ...column,
                        show: true
                    } : column;
                    });
            });

        }
        setStateColumns(newStateColumns);
    }


    const handleBulkSubmit = (event) => {
        let updatedData = props.data;

        if(currentSelectedState){
            let filteredSelectedState = {...currentSelectedState};
            if(event.excludedUsers){
                filteredSelectedState = {...currentSelectedState, ...event.excludedUsers};
            }

            //Update changed fields
            if( event && event.fields){

                Object.keys(filteredSelectedState).forEach(selectedRowId => {
                    if(filteredSelectedState[selectedRowId]){
                    
                        //Update skill value for given users & skill
                        Object.keys(event.fields).forEach(skillFieldName => {
                            updatedData = updatedData.map(row => {

                                //Update selectected row that has a value for the field (queue activation may only for those that are member of queue)
                                if(row.id === selectedRowId && skillFieldName in row){
                                    const newRow = JSON.parse(JSON.stringify(row));

                                    newRow[skillFieldName] = event.fields[skillFieldName];
                                    
                                    if(!newRow.changedItems){
                                        newRow.changedItems = [];
                                    } else {
                                        newRow.changedItems = newRow.changedItems.filter(id => {
                                            return newRow[id] !== newRow.originalValues[id];
                                        });
                                    }

                                    if (!newRow.originalValues || newRow.originalValues[skillFieldName] !== newRow[skillFieldName]) {
                                        newRow.changedItems.push(skillFieldName);
                                    }

                                    return newRow;
                                }
                                return row;
                            });
                        });
                    }
                });
            }

            //Update removed
            if(event && event.removedFields){
                Object.keys(filteredSelectedState).forEach(selectedRowId => {
                    if(filteredSelectedState[selectedRowId]){
                        
                        //Update skill value for given role & skill
                        Object.keys(event.removedFields).forEach(skillFieldName => {
                            updatedData = updatedData.map(row => {
                                if(row.id === selectedRowId){
                                    const newRow = JSON.parse(JSON.stringify(row));
    
                                    if(event.removedFields[skillFieldName]){
                                        newRow[skillFieldName] = "";
                                    }
    
                                    if(!newRow.changedItems){
                                        newRow.changedItems = [];
                                    } else {
                                        newRow.changedItems = newRow.changedItems.filter(id => {
                                            return newRow[id] !== newRow.originalValues[id];
                                        });
                                    }
    
                                    if (!newRow.originalValues || newRow.originalValues[skillFieldName] !== newRow[skillFieldName]) {
                                        newRow.changedItems.push(skillFieldName);
                                    }
                                
                                    return newRow;
                                }
                                return row;
                            });
                        });
                    }
                });
            }
        }

        if(event) { 
            updateColumnVisibility(event); 
        }

        props.onChange(updatedData);

        setBulkEditVisible(false);
        //Reset selected rows
        setCurrentSelectedState({});
    }

    const handleSelectedDataRemoved = (event) => {
        if(event.target && event.target.props && event.target.props.value && event.target.props.value in currentSelectedState){
           const newSelectedState = {...currentSelectedState};

           //Removed -> set to selectedState to false
           newSelectedState[event.target.props.value] = false;

           setCurrentSelectedState(newSelectedState);
        }
    }

    const checkHeaderSelectionValue = () => {
        let pageDataItems = [];
        if(!dataResult.data) return false; 

        dataResult.data.forEach((item) => {
            pageDataItems.push(item);
        });

        return (
            dataResult.data.length > 0 &&
          pageDataItems.filter((item) => item[SELECTED_FIELD]).length === pageDataItems.length
        );
    };

    const getHeaderColumnClassName = (props)  => {
        let headerStyles = " ";

        if(!props.rotated){
            headerStyles += styles['no-rotate'];
        } else if(props.type === 'boolean'){
            headerStyles += styles.rotateSwitch;
        } else {
            headerStyles += styles.rotate;
        }

        if(isColumnMenuFilterActive(props.field, dataState.filter)){
            headerStyles += " " + styles.active;
        }

        return headerStyles; // styles.rotate + " " + styles.active;
    };

    const gridProps = {
        style:{
//            initialWidth: "100%"
            maxHeight: props.gridMaxHeight ? props.gridMaxHeight : '',
//              maxHeight: 0.70 * window.innerHeight,
            width: props.gridWidth? props.gridWidth : ''
        },
        pageable: {
            buttonCount: 5,
            info: true,
            type: "numeric",
            pageSizes: [10, 15, 20, 50, 100, "All"],
            pageSizeValue: dataState.take === props.data.length ? "All" : dataState.take,
            previousNext: true,
          },
        resizable: true, //Do not activate. Bugged combined with window.resize //bug only occurs when strict mode is on

        reorderable: false,     //Doesn't work with tilted/rotated table headers currently
        sortable: true,
        size: 'small',          // Only works in newer version of grid - using className on grid instead 
        data: dataResult ? setSelectedValue(dataResult) : {},
        
        filterOperators: FILTER_OPERATORS,
        scrollable: "scrollable",
        editField: EDIT_FIELD,
        dataItemKey: DATA_ITEM_KEY,
        selectedField: SELECTED_FIELD,
        onItemChange: onItemChangedHandler,
        onSelectionChange: onSelectionChange,
        onHeaderSelectionChange: onHeaderSelectionChange,
        onDataStateChange: onDataStateChange,
    };

    if (props.cellRender) gridProps.cellRender = props.cellRender;
    if (props.rowRender) gridProps.rowRender = props.rowRender;

    return (
        <div id={props.gridId}>

                <Grid className={styles.lessPadding}
                    key={gridUpdateColumnWorkAroundKey}
                        {...gridProps} 
                        {...dataState}
                >
                    <GridNoRecords >
                        <div className={styles.norecords}>No records available.<br/>Please review your filter or apply one.</div>
                    </GridNoRecords>
                    <GridToolbar className={styles.GridToolBar}>
                        {props.filterColumnsEnabled && <CustomColumnMenu
                            {...props}
                            columns={stateColumns.filter((col) => !col.locked)}
                            onColumnsSubmit={onColumnsSubmit}
                            onColumnsReset={onColumnsReset}
                        />}

                        {!props.readOnly && <button className={styles.button}
                            onClick={toggleBulkEditDialog}
                            disabled={!bulkEditEnabled}
                        >
                            Bulk Edit
                        </button>}
                    </GridToolbar>
                    
                    
                    {props.selectionEnabled && <Column
                        field={SELECTED_FIELD}
                        width="35px"
                        headerSelectionValue={checkHeaderSelectionValue()}
                        editor='boolean'
                    />}

                    {stateColumns.map(
                        (column, idx) =>
                            column.show && (
                                <Column
                                    {...column}
                                    key={column.colKey}
//                                    cells={{headerCell: HeaderCell}}  /* Replacement for deprecated headerCell - needs work on the HeaderCell to be able to use */
                                    headerCell={HeaderCell}  /* Used for tooltip - Filter click doesn't work currently  */
                                    headerClassName={getHeaderColumnClassName(column)}
                                    filter={column.filter ? column.filter : 'text'}
                                    columnMenu={ColumnMenu}
                                />
                            )
                        )}
                </Grid>
            {bulkEditVisible && 
                <BulkEditForm 
                    cancelEdit={handleCancelBulkEdit} 
                    onSubmit={handleBulkSubmit} 
                    fields={stateColumns}
                    rows={currentSelectedState}
                    selectedData={selectedData}
                    title={"Bulk Edit"}
                    editFieldsTitle={props.title}
                    inputFieldTitle={props.inputFieldTitle}
                    addFieldTitle={props.addFieldTitle}
                    onSelectedDataRemoved={handleSelectedDataRemoved} 
//                    onAdditionalFieldSelected={handleFieldSelectedToShow}
                    removeEnabled={props.bulkRemoveEnabled}
                />
            }
        </div>
    );
}

export default GridComponent;

const generateColumns = (columnConfigs, setMinWidth) => {
//console.log("----GridComponent - Generate Columns - minwidth: " + setMinWidth);
//    console.log(columnConfigs);
    
    return columnConfigs.map(config => {
        if (config.width) {
            return (<Column key={config.key} {...config}/>);
        } else if (setMinWidth) {
            if (config.minWidth) {
                return (<Column key={config.key} {...config} width={config.minWidth}/>)    
            } else if (config.minResizableWidth) {
                return (<Column key={config.key} {...config} width={config.minResizableWidth}/>) 
            }
        }
        return (<Column key={config.key} {...config} />);
    });
}


const hasSelectedRows = (selectedRows) => {
    if(!selectedRows) return false;

    let filtered = Object.keys(selectedRows).filter( el => selectedRows[el] === true);

    if(!filtered) return false;

    return filtered.length > 0;
}

const HeaderCell = (props) => (
          <span className="k-cell-inner" title={props.title} >
            <span className="k-link" onClick={props.onClick}>
              <span className="k-column-title">
                {props.title}
                {props.children}
              </span>
            </span>
            <GridColumnMenuWrapper
                {...props.columnMenuWrapperProps}
            ></GridColumnMenuWrapper>
          </span>
);

